import React, { useState, useEffect, Suspense, lazy } from 'react';
import { HashRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import FloatingControls from './components/FloatingControls';
import Chatbot from './components/Chatbot';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import { NAV_LINKS } from './constants';

// Lazy loading des pages pour la performance
const Home = lazy(() => import('./pages/Home'));
const Services = lazy(() => import('./pages/Services'));
const Training = lazy(() => import('./pages/Training'));
const Software = lazy(() => import('./pages/Software'));
const Contact = lazy(() => import('./pages/Contact'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const LegalMentions = lazy(() => import('./pages/Legal').then(m => ({ default: m.LegalMentions })));
const PrivacyPolicy = lazy(() => import('./pages/Legal').then(m => ({ default: m.PrivacyPolicy })));
const Terms = lazy(() => import('./pages/Legal').then(m => ({ default: m.Terms })));

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

// Loader simple en attendant les pages
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-white dark:bg-be-darkest">
    <div className="w-12 h-12 border-4 border-be-france border-t-transparent rounded-full animate-spin"></div>
  </div>
);

const AppContent: React.FC = () => {
  const [isDark, setIsDark] = useState(false);
  const { language } = useLanguage();

  useEffect(() => {
    const root = window.document.documentElement;
    if (isDark) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [isDark]);

  const toggleTheme = () => setIsDark(!isDark);

  return (
    <Router>
      <ScrollToTop />
      <div className="flex flex-col min-h-screen">
        <Navbar 
          links={NAV_LINKS[language]} 
          isDark={isDark} 
          toggleTheme={toggleTheme} 
        />
        
        <main className="flex-grow relative">
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/services" element={<Services />} />
              <Route path="/logiciels" element={<Software />} />
              <Route path="/formations" element={<Training />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/legal" element={<LegalMentions />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<Terms />} />
            </Routes>
          </Suspense>
        </main>

        <Footer />
        <FloatingControls />
        <Chatbot />
      </div>
    </Router>
  );
};

const App: React.FC = () => {
  return (
    <LanguageProvider>
      <AppContent />
    </LanguageProvider>
  );
};

export default App;